import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { useManagedAgentProvisioningStatus } from '../hooks/useManagedAgentProvisioningStatus';
import type {
  ManagedAgentProvisioningStatusDTO,
  SetupReason,
} from '../lib/managedAgentProvisioningStatus';
import { useAuthStore } from '../stores/useAuthStore';
import styles from './ManagedAgentProvisioningStatus.module.css';

type IconName = 'check' | 'clock' | 'loader' | 'alert' | 'offline' | 'info' | 'refresh' | 'lock';

const iconPaths: Record<IconName, string> = {
  check: 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0M8 12l2.5 2.5L16 9',
  clock: 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0M12 7v5l3 2',
  loader: 'M21 12a9 9 0 1 1-6.2-8.55',
  alert: 'm10.3 3.9-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3.1l-8-14a2 2 0 0 0-3.4 0ZM12 9v4m0 4h.01',
  offline:
    'm2 2 20 20M5 8a12 12 0 0 1 2-1m4-1a12 12 0 0 1 10 3M5 13a10 10 0 0 1 7-3m4 1a10 10 0 0 1 3 2m-11 4a6 6 0 0 1 8 0m-4 3h.01',
  info: 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0M12 11v6m0-10h.01',
  refresh: 'M20 7v5h-5M4 17v-5h5M6 7a7 7 0 0 1 11.5-2L20 8M4 16l2.5 3A7 7 0 0 0 18 17',
  lock: 'M5 10h14v11H5ZM8 10V6a4 4 0 0 1 8 0v4',
};

export const SetupStatusIcon = ({
  name,
  spinning = false,
}: {
  name: IconName;
  spinning?: boolean;
}) => (
  <svg
    className={`${styles.icon}${spinning ? ` ${styles.spin}` : ''}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={iconPaths[name]} />
  </svg>
);

const setupReasons: Record<SetupReason, MessageDescriptor> = {
  node_missing: msg`Node.js is not available on this computer. Install Node.js 22 or later.`,
  npm_missing: msg`npm is not available on this computer. Install npm with Node.js.`,
  node_version_unsupported: msg`The computer needs Node.js 22 or later. Update Node.js on this computer.`,
  node_probe_failed: msg`The computer could not check its Node.js installation. Check that Node.js runs correctly.`,
  platform_unsupported: msg`Automatic runtime installation is not supported on this computer’s platform.`,
  installation_failed: msg`The runtime could not be installed. Check the computer’s internet connection and package access.`,
  installation_timeout: msg`Runtime installation took too long. Check the computer’s internet connection.`,
  verification_failed: msg`The installed runtime could not be verified. Check the helper logs on this computer.`,
  apply_failed: msg`The computer could not apply the saved Agent configuration. Check the helper logs on this computer.`,
  apply_timeout: msg`Applying the saved Agent configuration took too long. Check the helper logs on this computer.`,
};

const runtimeReasons = {
  api_key_invalid: [
    msg`Runtime credentials rejected`,
    msg`The runtime reports that its API credentials are invalid.`,
  ],
  quota_exceeded: [
    msg`Runtime quota exhausted`,
    msg`The runtime reports that its quota is exhausted.`,
  ],
  network_unreachable: [
    msg`Runtime network unavailable`,
    msg`The runtime cannot reach a required service.`,
  ],
  runtime_crashed: [
    msg`Runtime stopped unexpectedly`,
    msg`The runtime reports an unexpected stop.`,
  ],
  runtime_timeout: [msg`Runtime response timed out`, msg`The runtime did not respond in time.`],
  unknown: [
    msg`Runtime needs attention`,
    msg`The runtime reports an error. Check its connection settings and local logs.`,
  ],
} as const;

interface Presentation {
  title: string;
  description: string;
  tone: 'neutral' | 'warning' | 'error' | 'success';
  icon: IconName;
}

const setupStateLabels = {
  pending_assignment: msg`Pending assignment`,
  checking: msg`Checking`,
  installing: msg`Installing`,
  verifying: msg`Verifying`,
  waiting_to_apply: msg`Waiting to apply`,
  applying: msg`Applying`,
  failed: msg`Failed`,
  applied: msg`Applied`,
};

const activeTitles = {
  checking: msg`Checking this computer`,
  installing: msg`Installing the agent runtime`,
  verifying: msg`Verifying the agent runtime`,
  applying: msg`Applying Agent configuration`,
} as const;

const presentation = (
  i18n: I18n,
  status: ManagedAgentProvisioningStatusDTO,
  elapsed: number,
): Presentation => {
  const neutral = (title: string, description: string): Presentation => ({
    title,
    description,
    tone: 'neutral',
    icon: 'clock',
  });
  const attention = (title: string, description: string): Presentation => ({
    title,
    description,
    tone: 'warning',
    icon: 'clock',
  });
  if (status.desired === 'disabled')
    return neutral(
      t(i18n)`Agent disabled`,
      t(i18n)`Enable this Agent to resume setup on its computer.`,
    );
  if (status.desired === 'unbound')
    return neutral(
      t(i18n)`Not managed by a computer`,
      t(
        i18n,
      )`This Agent has no managed computer assigned. It can connect directly using its runtime.`,
    );
  if (status.desired === 'machine_missing')
    return attention(
      t(i18n)`The selected computer is no longer available`,
      t(i18n)`Review this Agent’s computer configuration.`,
    );
  if (status.helper === 'offline')
    return {
      ...attention(
        t(i18n)`Your computer is offline`,
        t(
          i18n,
        )`Setup status is unavailable. Keep the computer awake and check that Borgee’s helper is running.`,
      ),
      icon: 'offline',
    };
  if (status.observation === 'unsupported')
    return {
      ...neutral(
        t(i18n)`Setup status unavailable`,
        t(
          i18n,
        )`This Helper version does not report setup progress. Update the Borgee helper on your computer to see setup progress.`,
      ),
      icon: 'info',
    };
  if (status.observation === 'stale')
    return attention(
      t(i18n)`No recent setup update from this computer`,
      t(i18n)`Check that your computer is awake and its internet connection is working.`,
    );
  if (status.observation === 'unreported' || status.observation === 'awaiting_assignment')
    return neutral(
      t(i18n)`Waiting for your computer`,
      t(i18n)`Waiting for the computer to report this configuration.`,
    );
  if (status.sync_reason_code)
    return {
      title: t(i18n)`The computer could not read its Agent configuration`,
      description: t(
        i18n,
      )`The saved configuration has not been acknowledged. Check the helper logs on this computer.`,
      tone: 'error',
      icon: 'alert',
    };
  const setup = status.setup;
  if (!setup || setup.state === 'pending_assignment')
    return neutral(
      t(i18n)`Waiting for your computer`,
      t(
        i18n,
      )`This configuration is waiting for the computer’s next setup attempt. The computer has not started this setup request.`,
    );
  if (setup.state === 'failed')
    return {
      title: status.runtime.connected ? t(i18n)`Current setup failed` : t(i18n)`Agent setup failed`,
      description: i18n._(setupReasons[setup.reason_code]),
      tone: 'error',
      icon: 'alert',
    };
  if (setup.state === 'waiting_to_apply')
    return neutral(
      t(i18n)`Waiting for your computer to apply the setup`,
      t(
        i18n,
      )`The runtime installation is verified. Your saved setup is waiting for the next computer update.`,
    );
  if (setup.state === 'applied') {
    if (status.runtime.connected)
      return {
        title: t(i18n)`Setup accepted by your computer`,
        description: t(i18n)`Configuration accepted. Agent connected to Borgee.`,
        tone: 'success',
        icon: 'check',
      };
    if (setup.phase_age_ms + elapsed >= 60_000)
      return attention(
        t(i18n)`Agent is currently offline`,
        t(
          i18n,
        )`Configuration was accepted earlier; Agent is currently offline. Check runtime connection settings.`,
      );
    return neutral(
      t(i18n)`Setup accepted by your computer`,
      t(i18n)`Configuration accepted. Waiting for the Agent to connect.`,
    );
  }
  if (setup.work_age_ms + elapsed >= 480_000)
    return attention(
      t(i18n)`Setup is taking longer than expected`,
      t(i18n)`The computer still reports work in progress.`,
    );
  return {
    title: i18n._(activeTitles[setup.state]),
    description: t(i18n)`Your computer is preparing the saved Agent configuration.`,
    tone: 'neutral',
    icon: 'loader',
  };
};

const timeLabel = (i18n: I18n, timestamp: string) =>
  new Date(timestamp).toLocaleTimeString(i18n.locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const retryLabel = (i18n: I18n, delay: number) => {
  const seconds = Math.max(0, Math.ceil(delay / 1000));
  return seconds > 0
    ? t(
        i18n,
      )`Automatic retry eligible in about ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
    : t(i18n)`Automatic retry is eligible now; waiting for a computer update.`;
};

interface Props {
  agentId: string;
  ownerId: string | null;
  agentName: string;
  variant?: 'profile' | 'creation';
  onViewAgent?: () => void;
  onOpenChat?: () => void;
  onClose?: () => void;
}

export const ManagedAgentRuntimeConnection = ({
  agentId,
  ownerId,
}: Pick<Props, 'agentId' | 'ownerId'>) => {
  const { i18n } = useLingui();
  const poll = useManagedAgentProvisioningStatus(agentId, ownerId);
  const current = poll.readState === 'ready' ? poll.status : null;
  const connection = current ? (current.runtime.connected ? 'connected' : 'offline') : 'unknown';
  return (
    <span className={styles.connectionBadge}>
      <span className={styles.presence} data-connection={connection} aria-hidden="true" />
      {connection === 'unknown'
        ? t(i18n)`Connection status unavailable`
        : connection === 'connected'
          ? t(i18n)`Connected to Borgee`
          : t(i18n)`Offline`}
      {current?.runtime.state === 'error' && (
        <span className={styles.runtimeAttention}>
          <Trans>Runtime needs attention</Trans>
        </span>
      )}
    </span>
  );
};

export default function ManagedAgentProvisioningStatus({
  agentId,
  ownerId,
  agentName,
  variant = 'profile',
  onViewAgent,
  onOpenChat,
  onClose,
}: Props) {
  const { i18n } = useLingui();
  const currentUser = useAuthStore((state) => state.currentUser);
  const poll = useManagedAgentProvisioningStatus(agentId, ownerId);
  if (currentUser?.id !== ownerId || currentUser.role !== 'member') return null;

  const current = poll.readState === 'ready' ? poll.status : null;
  const elapsed = Math.max(0, poll.now - poll.receivedAt);
  const view: Presentation = current
    ? presentation(i18n, current, elapsed)
    : poll.readState === 'disconnected'
      ? {
          title: t(i18n)`You’re offline`,
          description: t(
            i18n,
          )`Setup status cannot be refreshed. Updates resume when this browser reconnects.`,
          tone: 'warning',
          icon: 'offline',
        }
      : poll.readState === 'error'
        ? {
            title: t(i18n)`Could not refresh setup status`,
            description: t(
              i18n,
            )`Try refreshing the status again. The Agent stays saved in your workspace.`,
            tone: 'warning',
            icon: 'alert',
          }
        : {
            title: t(i18n)`Waiting for setup status`,
            description: t(i18n)`Checking the current setup status of this Agent.`,
            tone: 'neutral',
            icon: 'clock',
          };
  const setup = current?.setup;
  const failed = setup?.state === 'failed';
  const retry =
    current?.observation === 'fresh' && setup && 'retry_in_ms' in setup
      ? setup.retry_in_ms
      : undefined;
  const connection = current ? (current.runtime.connected ? 'connected' : 'offline') : 'unknown';
  const runtimeError = current?.runtime.state === 'error' ? current.runtime : null;
  const runtimeCopy = runtimeError ? runtimeReasons[runtimeError.reason] : null;
  const refresh = (
    <button
      type="button"
      className="btn btn-sm"
      onClick={poll.refresh}
      disabled={poll.refreshing}
      aria-label={t(i18n)`Refresh status`}
      title={t(i18n)`Refresh status`}
    >
      <SetupStatusIcon name="refresh" />
      {variant === 'profile' ? t(i18n)`Refresh status` : null}
    </button>
  );

  return (
    <div
      className={`${styles.panel} ${variant === 'profile' ? styles.profile : ''}`}
      data-testid="managed-agent-provisioning-status"
    >
      {variant === 'profile' && (
        <div className={styles.panelHeader}>
          <div className="agent-profile-field-label">
            <Trans>SETUP</Trans>
          </div>
          {refresh}
        </div>
      )}
      <section className={styles.card} data-tone={view.tone} aria-label={t(i18n)`Setup status`}>
        <div className={styles.heading} role="status" aria-live="polite">
          <SetupStatusIcon name={view.icon} spinning={view.icon === 'loader'} />
          <span>{view.title}</span>
        </div>
        <p className={styles.description}>{view.description}</p>
        {failed && current?.runtime.connected && (
          <p className={styles.actionNote}>
            <Trans>
              The active connection does not confirm that the new settings were applied.
            </Trans>
          </p>
        )}
        {retry !== undefined && (
          <p className={styles.actionNote} data-testid="setup-retry-countdown">
            {retryLabel(i18n, retry - elapsed)}
          </p>
        )}
        {current?.observation === 'stale' && setup && (
          <p className={styles.actionNote}>
            <Trans>Last observed setup: {i18n._(setupStateLabels[setup.state])}.</Trans>
          </p>
        )}
        {current?.observed_at && (
          <div className={styles.meta}>
            {current.observation === 'stale' ? t(i18n)`Last observed report` : t(i18n)`Last report`}
            : <time dateTime={current.observed_at}>{timeLabel(i18n, current.observed_at)}</time>
          </div>
        )}
        {setup?.state === 'applied' && (
          <div className={styles.meta}>
            {setup.accepted_at ? (
              <>
                <Trans>
                  Configuration accepted approximately at{' '}
                  <time dateTime={setup.accepted_at}>{timeLabel(i18n, setup.accepted_at)}</time>
                </Trans>
              </>
            ) : setup.phase_age_ms >= 86_400_000 ? (
              t(i18n)`Configuration accepted at least 24 hours ago`
            ) : null}
          </div>
        )}
        {(failed || current?.sync_reason_code) && (
          <details className={styles.details}>
            <summary>
              <Trans>Setup details</Trans>
            </summary>
            <dl className={styles.detailsGrid}>
              <dt>
                <Trans>Reason</Trans>
              </dt>
              <dd>
                <code>{failed ? setup.reason_code : current?.sync_reason_code}</code>
              </dd>
              {failed && (
                <>
                  <dt>
                    <Trans>Attempt</Trans>
                  </dt>
                  <dd>{i18n.number(setup.attempt)}</dd>
                </>
              )}
            </dl>
          </details>
        )}
      </section>
      {runtimeCopy && runtimeError && (
        <section
          className={`${styles.card} ${styles.runtimeCard}`}
          data-tone="error"
          aria-label={t(i18n)`Runtime status`}
        >
          <div className={styles.heading}>
            <SetupStatusIcon name="alert" />
            <span>{i18n._(runtimeCopy[0])}</span>
          </div>
          <p className={styles.description}>{i18n._(runtimeCopy[1])}</p>
          <details className={styles.details}>
            <summary>
              <Trans>Runtime details</Trans>
            </summary>
            <dl className={styles.detailsGrid}>
              <dt>
                <Trans>Reason</Trans>
              </dt>
              <dd>
                <code>{runtimeError.reason}</code>
              </dd>
            </dl>
          </details>
        </section>
      )}
      <div className={styles.connectionLine}>
        <span className={styles.presence} data-connection={connection} aria-hidden="true" />
        {connection === 'unknown'
          ? t(i18n)`Connection status unavailable`
          : connection === 'connected'
            ? t(i18n)`${agentName} is connected to Borgee`
            : t(i18n)`${agentName} is offline`}
        {failed && connection === 'connected' ? t(i18n)` · Setup still needs attention` : ''}
      </div>
      <p className={styles.ownerHint}>
        <SetupStatusIcon name="lock" />
        <Trans>Only you, the Agent owner, can see setup details.</Trans>
      </p>
      {variant === 'creation' && (
        <>
          <div className={styles.footer}>
            {refresh}
            <button
              type="button"
              className={`btn btn-sm${connection === 'connected' ? '' : ' btn-primary'}`}
              onClick={onViewAgent}
            >
              <Trans>View Agent</Trans>
            </button>
            {connection === 'connected' ? (
              <button type="button" className="btn btn-sm btn-primary" onClick={onOpenChat}>
                <Trans>Open chat</Trans>
              </button>
            ) : (
              <button type="button" className="btn btn-sm" onClick={onClose}>
                <Trans>Close</Trans>
              </button>
            )}
          </div>
          <p className={styles.exitNote}>
            <Trans>You can close this window. {agentName} will stay in your workspace.</Trans>
          </p>
        </>
      )}
    </div>
  );
}
